/**
 * MetricsReport — headline sales KPIs (Reports → Metrics) for the leads that
 * were CREATED in the selected period (a true cohort): speed-to-lead, reach
 * rate, call connect rate, lead→randomized, and pipeline cycle time. Scoped by
 * the global study filter. Plain SummaryStat cards — no chart library.
 */
import { useEffect, useState } from 'react';
import api from '../../../services/api';
import { SalesSummary } from '../../types';
import { SummaryStat, fmtDurationLong } from './StatBlocks';

interface Props {
  from: string | null;
  to: string | null;
  studyId?: string | null;
}

const pct = (v: number | null) => (v === null || v === undefined ? '—' : `${v}%`);
const days = (v: number | null) => (v === null || v === undefined ? '—' : `${v.toFixed(1)} d`);

export default function MetricsReport({ from, to, studyId = null }: Props) {
  const [s, setS] = useState<SalesSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get('/api/crm/analytics/sales-summary', {
        params: { from: from || undefined, to: to || undefined, study_id: studyId || undefined },
      })
      .then((res) => {
        if (alive) {
          setS(res.data?.summary || null);
          setError(null);
        }
      })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.error || 'Failed to load metrics');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to, studyId]);

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading metrics…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!s || s.leads === 0)
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No leads created in this range yet.</p>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3">
        <SummaryStat label="Leads (cohort)" value={s.leads.toLocaleString()} sub="created in range" />
        <SummaryStat
          label="Reach rate"
          value={pct(s.reach_pct)}
          sub={`${s.reached.toLocaleString()} ever replied`}
        />
        <SummaryStat
          label="Speed to lead (median)"
          value={fmtDurationLong(s.speed_median_sec)}
          sub={
            s.speed_within_15min_pct !== null
              ? `${s.speed_within_15min_pct}% within 15 min · p90 ${fmtDurationLong(s.speed_p90_sec)}`
              : 'first outbound touch'
          }
        />
        <SummaryStat
          label="Call connect rate"
          value={pct(s.connect_pct)}
          sub={`${s.connected_calls.toLocaleString()} / ${s.outbound_calls.toLocaleString()} calls`}
        />
        <SummaryStat
          label="Lead → randomized"
          value={pct(s.lead_to_rand_pct)}
          sub={`${s.randomized.toLocaleString()} randomized`}
          highlight={s.randomized > 0}
        />
        <SummaryStat
          label="Cycle time (median)"
          value={days(s.cycle_median_days)}
          sub="lead → randomized"
        />
      </div>
      <p className="px-1 text-[11px] leading-snug text-gray-400">
        Cohort = leads created in the selected range. Speed-to-lead counts the first outbound touch (including the
        automated welcome). Booking/show/screen-fail rates and per-stage velocity live in the Conversion tab.
      </p>
    </div>
  );
}
